// @flow
import { of } from 'rxjs';
import { map, catchError, mergeMap, debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { setNewAlert } from '../alert/duck';
import { cargoPostContactMessageErrorFn } from '../cargo/duck';
import {
  getUserWishListsApi, postUserWishListsApi, putUserWishListsApi, deleteUserWishListsApi, endpoints,
  uploadImageApi, uploadImageURLApi,
} from './api';
import { ofType, StateObservable } from 'redux-observable';
import update from 'immutability-helper';
import Paginator from '../../common/Paginator';

import type { Observable } from 'rxjs';
import type { WishList } from '../../entities';
import type { HTTPError } from '../../error';
import type { RootState } from '../index';
import { dataURItoBlob } from '../../utils/file';

export type WishListBodyRequest = {
  name: string;
  image?: string;
  imageURI?: string;
  imageId?: string;
  type: string;
};

export type MyWishListsAction = {
  +type: string;
  +wishLists?: WishList[];
  +imageId?: string;
  +error?: HTTPError;
  +wishListReq?: WishListBodyRequest;
  +userId?: string;
  +slug?: string;
  +resetPagination?: boolean;
};

export type MyWishListsState = {
  isFetchingWishLists?: boolean;
  wishLists?: WishList[];
  wishListsPage?: number;
  wishListsPageCanAsk?: boolean;
  fetchWishListsError?: HTTPError;

  isPostingWishList?: boolean;
  postWishListSuccess?: boolean;
  postWishListError?: HTTPError;

  isPuttingWishList?: boolean;
  putWishListSuccess?: boolean;
  putWishListError?: HTTPError;

  isDeletingWishList?: boolean;
  deleteWishListSuccess?: boolean;
  deleteWishListError?: HTTPError;
};

// Actions
const MY_WISH_LISTS_FETCH_WISH_LISTS_REQUEST = 'MY_WISH_LISTS_FETCH_WISH_LISTS_REQUEST';
const MY_WISH_LISTS_FETCH_WISH_LISTS_SUCCESS = 'MY_WISH_LISTS_FETCH_WISH_LISTS_SUCCESS';
const MY_WISH_LISTS_FETCH_WISH_LISTS_ERROR = 'MY_WISH_LISTS_FETCH_WISH_LISTS_ERROR';

const MY_WISH_LISTS_POST_WISH_LIST_REQUEST = 'MY_WISH_LISTS_POST_WISH_LIST_REQUEST';
const MY_WISH_LISTS_POST_WISH_LIST_IMAGE_SUCCESS = 'MY_WISH_LISTS_POST_WISH_LIST_IMAGE_SUCCESS';
const MY_WISH_LISTS_POST_WISH_LIST_SUCCESS = 'MY_WISH_LISTS_POST_WISH_LIST_SUCCESS';
const MY_WISH_LISTS_POST_WISH_LIST_ERROR = 'MY_WISH_LISTS_POST_WISH_LIST_ERROR';

const MY_WISH_LISTS_PUT_WISH_LIST_REQUEST = 'MY_WISH_LISTS_PUT_WISH_LIST_REQUEST';
const MY_WISH_LISTS_PUT_WISH_LIST_IMAGE_SUCCESS = 'MY_WISH_LISTS_PUT_WISH_LIST_IMAGE_SUCCESS';
const MY_WISH_LISTS_PUT_WISH_LIST_SUCCESS = 'MY_WISH_LISTS_PUT_WISH_LIST_SUCCESS';
const MY_WISH_LISTS_PUT_WISH_LIST_ERROR = 'MY_WISH_LISTS_PUT_WISH_LIST_ERROR';

const MY_WISH_LISTS_DELETE_WISH_LIST_REQUEST = 'MY_WISH_LISTS_DELETE_WISH_LIST_REQUEST';
const MY_WISH_LISTS_DELETE_WISH_LIST_SUCCESS = 'MY_WISH_LISTS_DELETE_WISH_LIST_SUCCESS';
const MY_WISH_LISTS_DELETE_WISH_LIST_ERROR = 'MY_WISH_LISTS_DELETE_WISH_LIST_ERROR';

const MY_WISH_LISTS_RESET_STATE = 'MY_WISH_LISTS_RESET_STATE';

// Action Creators
export function fetchWishLists(resetPagination?: boolean): MyWishListsAction {
  return {
    type: MY_WISH_LISTS_FETCH_WISH_LISTS_REQUEST,
    resetPagination,
  };
}

export function fetchWishListsSuccess(wishLists: WishList): MyWishListsAction {
  return {
    type: MY_WISH_LISTS_FETCH_WISH_LISTS_SUCCESS,
    wishLists,
  };
}

export function fetchWishListsErrorFn(error: HTTPError): MyWishListsAction {
  return {
    type: MY_WISH_LISTS_FETCH_WISH_LISTS_ERROR,
    error,
  };
}

export function postWishList(wishListReq: WishListBodyRequest): MyWishListsAction {
  return {
    type: MY_WISH_LISTS_POST_WISH_LIST_REQUEST,
    wishListReq,
  };
}

export function postWishListImageSuccess(imageId: string, wishListReq: WishListBodyRequest): MyWishListsAction {
  return {
    type: MY_WISH_LISTS_POST_WISH_LIST_IMAGE_SUCCESS,
    wishListReq,
    imageId,
  };
}

export function postWishListSuccessFn(): MyWishListsAction {
  return {
    type: MY_WISH_LISTS_POST_WISH_LIST_SUCCESS,
  };
}

export function postWishListErrorFn(error: HTTPError): MyWishListsAction {
  return {
    type: MY_WISH_LISTS_POST_WISH_LIST_ERROR,
    error,
  };
}

export function putWishList(wishListReq: WishListBodyRequest, userId: string, slug: string): MyWishListsAction {
  return {
    type: MY_WISH_LISTS_PUT_WISH_LIST_REQUEST,
    wishListReq,
    userId,
    slug,
  };
}

export function putWishListImageSuccess(wishListReq: WishListBodyRequest, userId: string, slug: string,
                                        imageId?: string): MyWishListsAction {
  return {
    type: MY_WISH_LISTS_PUT_WISH_LIST_IMAGE_SUCCESS,
    wishListReq,
    userId,
    slug,
    imageId,
  };
}

export function putWishListSuccessFn(): MyWishListsAction {
  return {
    type: MY_WISH_LISTS_PUT_WISH_LIST_SUCCESS,
  };
}

export function putWishListErrorFn(error: HTTPError): MyWishListsAction {
  return {
    type: MY_WISH_LISTS_PUT_WISH_LIST_ERROR,
    error,
  };
}

export function deleteWishList(userId: string, slug: string): MyWishListsAction {
  return {
    type: MY_WISH_LISTS_DELETE_WISH_LIST_REQUEST,
    userId,
    slug,
  };
}

export function deleteWishListSuccessFn(): MyWishListsAction {
  return {
    type: MY_WISH_LISTS_DELETE_WISH_LIST_SUCCESS,
  };
}

export function deleteWishListErrorFn(error: HTTPError): MyWishListsAction {
  return {
    type: MY_WISH_LISTS_DELETE_WISH_LIST_ERROR,
    error,
  };
}

export function resetState() {
  return {
    type: MY_WISH_LISTS_RESET_STATE,
  };
}

export const actions = {
  MY_WISH_LISTS_FETCH_WISH_LISTS_REQUEST,
  MY_WISH_LISTS_FETCH_WISH_LISTS_SUCCESS,
  MY_WISH_LISTS_FETCH_WISH_LISTS_ERROR,
  MY_WISH_LISTS_RESET_STATE,
  MY_WISH_LISTS_POST_WISH_LIST_REQUEST,
  MY_WISH_LISTS_POST_WISH_LIST_IMAGE_SUCCESS,
  MY_WISH_LISTS_POST_WISH_LIST_SUCCESS,
  MY_WISH_LISTS_POST_WISH_LIST_ERROR,
  MY_WISH_LISTS_PUT_WISH_LIST_REQUEST,
  MY_WISH_LISTS_PUT_WISH_LIST_IMAGE_SUCCESS,
  MY_WISH_LISTS_PUT_WISH_LIST_SUCCESS,
  MY_WISH_LISTS_PUT_WISH_LIST_ERROR,
  MY_WISH_LISTS_DELETE_WISH_LIST_REQUEST,
  MY_WISH_LISTS_DELETE_WISH_LIST_SUCCESS,
  MY_WISH_LISTS_DELETE_WISH_LIST_ERROR,
  fetchWishLists,
  fetchWishListsSuccess,
  fetchWishListsErrorFn,
  postWishList,
  postWishListImageSuccess,
  postWishListSuccessFn,
  postWishListErrorFn,
  putWishList,
  putWishListImageSuccess,
  putWishListSuccessFn,
  putWishListErrorFn,
  deleteWishList,
  deleteWishListSuccessFn,
  deleteWishListErrorFn,
  resetState,
};

export const initialState: MyWishListsState = {
  wishListsPage: 0,
};

const paginator = new Paginator(['wishLists'], 24);

// Reducer
export default function reducer(state: MyWishListsState = initialState,
                                action: MyWishListsAction): MyWishListsState {
  switch (action.type) {
    case MY_WISH_LISTS_FETCH_WISH_LISTS_REQUEST:
      if (action.resetPagination) {
        paginator.reset('wishLists');
        return update(state, {
          isFetchingWishLists: { $set: true },
          wishLists: { $set: paginator.getItems('wishLists') },
          wishListsPage: { $set: paginator.getPage('wishLists') },
          wishListsPageCanAsk: { $set: paginator.getCanAsk('wishLists') },
          $unset: ['fetchWishListsError'],
        });
      }
      return update(state, {
        isFetchingWishLists: { $set: true },
        $unset: ['fetchWishListsError'],
      });
    case MY_WISH_LISTS_FETCH_WISH_LISTS_SUCCESS:
      paginator.increment('wishLists', action.wishLists, state.wishLists, 'slug');
      return update(state, {
        $unset: ['isFetchingWishLists'],
        wishLists: { $set: paginator.getItems('wishLists') },
        wishListsPage: { $set: paginator.getPage('wishLists') },
        wishListsPageCanAsk: { $set: paginator.getCanAsk('wishLists') },
      });
    case MY_WISH_LISTS_FETCH_WISH_LISTS_ERROR:
      return update(state, {
        $unset: ['isFetchingWishLists'],
        fetchWishListsError: { $set: action.error },
        wishListsPage: { $set: state.wishListsPage },
      });
    case MY_WISH_LISTS_POST_WISH_LIST_REQUEST:
      return update(state, {
        isPostingWishList: { $set: true },
        $unset: ['postWishListError', 'postWishListSuccess'],
      });
    case MY_WISH_LISTS_POST_WISH_LIST_SUCCESS:
      return update(state, {
        $unset: ['isPostingWishList'],
        postWishListSuccess: { $set: true },
      });
    case MY_WISH_LISTS_POST_WISH_LIST_ERROR:
      return update(state, {
        $unset: ['isPostingWishList'],
        postWishListError: { $set: action.error },
      });
    case MY_WISH_LISTS_PUT_WISH_LIST_REQUEST:
      return update(state, {
        isPuttingWishList: { $set: true },
        $unset: ['putWishListError', 'putWishListSuccess'],
      });
    case MY_WISH_LISTS_PUT_WISH_LIST_SUCCESS:
      return update(state, {
        $unset: ['isPuttingWishList'],
        putWishListSuccess: { $set: true },
      });
    case MY_WISH_LISTS_PUT_WISH_LIST_ERROR:
      return update(state, {
        $unset: ['isPuttingWishList'],
        putWishListError: { $set: action.error },
      });
    case MY_WISH_LISTS_DELETE_WISH_LIST_REQUEST:
      return update(state, {
        isDeletingWishList: { $set: true },
        $unset: ['deleteWishListError', 'deleteWishListSuccess'],
      });
    case MY_WISH_LISTS_DELETE_WISH_LIST_SUCCESS:
      return update(state, {
        $unset: ['isDeletingWishList'],
        deleteWishListSuccess: { $set: true },
      });
    case MY_WISH_LISTS_DELETE_WISH_LIST_ERROR:
      return update(state, {
        $unset: ['isDeletingWishList'],
        deleteWishListError: { $set: action.error },
      });
    case MY_WISH_LISTS_RESET_STATE:
      paginator.reset('wishLists');
      return initialState;
    default:
      return state;
  }
}

export function fetchMyWishListsEpic(action$: Observable<MyWishListsAction>,
                                     state$: StateObservable<RootState>): Observable<MyWishListsAction> {
  return action$.pipe(
    ofType(MY_WISH_LISTS_FETCH_WISH_LISTS_REQUEST),
    debounceTime(600),
    switchMap(() => getUserWishListsApi({
      page: state$.value.myWishLists.wishListsPage || paginator.getPage('wishLists'),
      limit: paginator.getLimit(),
    }).pipe(
      takeUntil(action$.pipe(ofType(MY_WISH_LISTS_FETCH_WISH_LISTS_REQUEST))),
      takeUntil(action$.pipe(ofType(MY_WISH_LISTS_RESET_STATE))),
      map((wishLists: WishList[]) => fetchWishListsSuccess(wishLists)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(
            fetchWishListsErrorFn(e),
            setNewAlert('myWishLists', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(
            fetchWishListsErrorFn(e),
            setNewAlert('myWishLists', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(fetchWishListsErrorFn(e));
      }),
    )),
  );
}

export function postWishListImageEpic(action$: Observable<MyWishListsAction>) {
  return action$.pipe(
    ofType(MY_WISH_LISTS_POST_WISH_LIST_REQUEST),
    mergeMap(({ wishListReq }) => {
      const { imageURI } = wishListReq;
      let obs = of({});
      if (imageURI) {
        const fd = new FormData();
        fd.append('file', dataURItoBlob(imageURI));
        fd.append('resourceGroup', 'WISHLIST');
        obs = uploadImageApi(fd, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      return obs.pipe(
        map((imageResponse: { id: string }) => postWishListImageSuccess(imageResponse.id, wishListReq)),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(
              postWishListErrorFn(e),
              setNewAlert('myWishLists', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(
              postWishListErrorFn(e),
              setNewAlert('myWishLists', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postWishListErrorFn(e));
        }),
      )
    }),
  );
}

export function postWishListEpic(action$: Observable<MyWishListsAction>) {
  return action$.pipe(
    ofType(MY_WISH_LISTS_POST_WISH_LIST_IMAGE_SUCCESS),
    debounceTime(600),
    mergeMap(({ imageId, wishListReq }) => {
      if (!!wishListReq) {
        wishListReq.imageId = imageId;
        delete wishListReq.imageURI;
        delete wishListReq.image;
        return postUserWishListsApi(wishListReq)
          .pipe(
            map(() => postWishListSuccessFn()),
            catchError((e: HTTPError) => {
              if (Math.round(e.code / 100) === 5) {
                return of(
                  postWishListErrorFn(e),
                  setNewAlert('myWishLists', 'error', undefined, 'httpErrors.post.500'),
                );
              } else if (e.code === 400) {
                return of(
                  postWishListErrorFn(e),
                  setNewAlert('myWishLists', 'error', undefined, 'httpErrors.post.400'),
                );
              }
              return of(postWishListErrorFn(e));
            }),
          )
      }
    })
  );
}

export function putWishListImageEpic(action$: Observable<MyWishListsAction>) {
  return action$.pipe(
    ofType(MY_WISH_LISTS_PUT_WISH_LIST_REQUEST),
    debounceTime(600),
    mergeMap(({ wishListReq, userId, slug }) => {
      const { imageURI } = wishListReq;
      let obs = of({});
      if (imageURI) {
        const fd = new FormData();
        fd.append('file', dataURItoBlob(imageURI));
        fd.append('resourceGroup', 'WISHLIST');
        obs = uploadImageApi(fd, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        return of(putWishListImageSuccess(wishListReq, userId, slug))
      }
      return obs.pipe(
        map((imageResponse: { id: string }) =>
          putWishListImageSuccess(wishListReq, userId, slug, imageResponse.id)),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(
              putWishListErrorFn(e),
              setNewAlert('myWishLists', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(
              putWishListErrorFn(e),
              setNewAlert('myWishLists', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(putWishListErrorFn(e));
        }),
      )
    }),
  );
}

export function putWishListEpic(action$: Observable<MyWishListsAction>) {
  return action$.pipe(
    ofType(MY_WISH_LISTS_PUT_WISH_LIST_IMAGE_SUCCESS),
    debounceTime(600),
    mergeMap(({ wishListReq, userId, slug, imageId }) => {
      if (wishListReq && imageId) {
        wishListReq.imageId = imageId;
        delete wishListReq.imageURI;
        delete wishListReq.image;
      }
      return putUserWishListsApi(wishListReq, {
        route: endpoints.PUT_USER_WISH_LIST
          .replace(':userId', userId)
          .replace(':slug', slug),
      }).pipe(
        map(() => putWishListSuccessFn()),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(
              putWishListErrorFn(e),
              setNewAlert('myWishLists', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(
              putWishListErrorFn(e),
              setNewAlert('myWishLists', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(putWishListErrorFn(e));
        }),
      )
    }),
  );
}

export function deleteWishListEpic(action$: Observable<MyWishListsAction>) {
  return action$.pipe(
    ofType(MY_WISH_LISTS_DELETE_WISH_LIST_REQUEST),
    mergeMap(({ userId, slug }) => deleteUserWishListsApi(undefined, {
      route: endpoints.DELETE_USER_WISH_LIST
        .replace(':userId', userId)
        .replace(':slug', slug),
    }).pipe(
      map(() => deleteWishListSuccessFn()),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(
            deleteWishListErrorFn(e),
            setNewAlert('myWishLists', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(
            deleteWishListErrorFn(e),
            setNewAlert('myWishLists', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(deleteWishListErrorFn(e));
      }),
    )),
  );
}

export function fetchMyWishListsAfterChangeWisListsEpic(action$: Observable<MyWishListsAction>) {
  return action$.pipe(
    ofType(MY_WISH_LISTS_POST_WISH_LIST_SUCCESS, MY_WISH_LISTS_PUT_WISH_LIST_SUCCESS,
      MY_WISH_LISTS_DELETE_WISH_LIST_SUCCESS),
    map(() => fetchWishLists(true)),
  );
}



// WEBPACK FOOTER //
// ./src/modules/my-wish-lists/duck.js